import { useEffect, useState, type FormEvent } from 'react';
import { confirmPasswordReset, signInWithEmailAndPassword, verifyPasswordResetCode } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '../../lib/firebase.js';

const input =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-start outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10';
const primary =
  'w-full rounded-lg bg-slate-900 px-4 py-2.5 font-medium text-white transition hover:bg-slate-800 disabled:opacity-50';

/** The same floor the admin's "set password" enforces (`passwordSchema` in shared). */
const MIN_PASSWORD = 8;

/**
 * Where the password-reset link and the invite land (story 43): the app's
 * own page on the app's own domain, instead of Firebase's hosted action
 * handler. The one-time code in the URL is checked with the web SDK, the
 * new password is confirmed with it, and the person is signed in at once —
 * an invite ends on the home screen, not on a second sign-in form.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const code = new URLSearchParams(window.location.search).get('oobCode') ?? '';
  const [email, setEmail] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(code === '');
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<'mismatch' | 'short' | 'reset' | null>(null);

  useEffect(() => {
    if (code === '') return;
    let alive = true;
    verifyPasswordResetCode(auth, code)
      .then((address) => alive && setEmail(address))
      .catch(() => alive && setInvalid(true));
    return () => {
      alive = false;
    };
  }, [code]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setFailed(null);
    if (password.length < MIN_PASSWORD) {
      setFailed('short');
      return;
    }
    if (password !== again) {
      setFailed('mismatch');
      return;
    }
    setBusy(true);
    try {
      await confirmPasswordReset(auth, code, password);
      if (email) await signInWithEmailAndPassword(auth, email, password);
      // Signed in: the route table takes over and shows the home screen.
      window.location.assign('/');
    } catch {
      setFailed('reset');
      setBusy(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm space-y-5 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200"
        data-testid="reset-password-form"
        data-state={invalid ? 'invalid' : email ? 'ready' : 'checking'}
      >
        <header className="space-y-1">
          <h1 className="text-xl font-semibold text-slate-900">{t('app.title')}</h1>
          <p className="text-sm text-slate-500">{t('auth.resetTitle')}</p>
        </header>

        {invalid ? (
          <>
            <p role="alert" className="text-sm text-red-600" data-testid="reset-password-invalid">
              {t('auth.linkInvalid')}
            </p>
            <a href="/" className="block w-full text-center text-sm text-slate-600 underline-offset-4 hover:underline">
              {t('auth.backToSignIn')}
            </a>
          </>
        ) : (
          <>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">{t('auth.email')}</span>
              <input type="email" value={email ?? ''} readOnly dir="ltr" className={`${input} bg-slate-50`} />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">{t('auth.newPassword')}</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                dir="ltr"
                className={input}
                data-testid="reset-password-new"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-sm font-medium text-slate-700">{t('auth.newPasswordAgain')}</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                dir="ltr"
                className={input}
                data-testid="reset-password-again"
              />
            </label>
            {failed && (
              <p role="alert" className="text-sm text-red-600" data-testid="reset-password-error">
                {failed === 'mismatch'
                  ? t('auth.passwordMismatch')
                  : failed === 'short'
                    ? t('auth.passwordTooShort', { min: MIN_PASSWORD })
                    : t('auth.resetFailedFinal')}
              </p>
            )}
            <button type="submit" disabled={busy || email === null} className={primary} data-testid="reset-password-submit">
              {busy ? t('auth.signingIn') : t('auth.setPasswordAndSignIn')}
            </button>
          </>
        )}
      </form>
    </main>
  );
}
